import { List, Map } from "immutable";
import { createSelector } from "reselect";
import i18n from "~/i18n";
import { getGanttTaskLists } from "~/store/selectors/selector.ganttTaskLists";
import { dateToMySQLFormat, getDiffBetweenDates, getGanttDurationByDates } from "~/utils/date.jsx";
import { makeGanttLinkId, sortCompare } from "~/utils/utils.jsx";
import { createImmutableIsEqualSelector } from "./index.jsx";
import { getConfigOption } from "./selector.configOptions.jsx";
import { getSubtasksForGantt } from "./selector.subtasks.jsx";
import { getAllTasks, getTaskById, getOpenTasks, getTasksForGantt } from "./selector.tasks.jsx";
import { isLoggedUserClientSelector } from "./selector.users.jsx";

const getAllTaskDependencies = (state) => state.task_dependencies;

export const getAllTaskDependenciesParents = (state) => getAllTaskDependencies(state).parents;

export const getAllTaskDependenciesChildren = (state) => getAllTaskDependencies(state).children;

export const getSimulation = (state) => getAllTaskDependencies(state).simulation;

export const getSimulationTasks = createSelector(getSimulation, (simulation) => simulation.tasks);

export const getIsSimulationChanged = (state) => getSimulation(state).simulationChanged;

const defaultSuggestions = Map();
const getAllTaskDependenciesSuggestions = (state) => getAllTaskDependencies(state).suggestions || defaultSuggestions;

const defaultGroupedSuggestions = Map();
const getAllTaskDependenciesGroupedSuggestions = (state) =>
  getAllTaskDependencies(state).groupedSuggestions || defaultGroupedSuggestions;

const defaultRecentTasks = Map();
const getAllTaskDependenciesRecentTasks = (state) => getAllTaskDependencies(state).recentTasks || defaultRecentTasks;

const defaultParents = List();

export const makeGetAllParentsByTaskIdSelector = () =>
  createSelector(
    (state, id) => getAllTaskDependenciesParents(state).get(`${id}`, defaultParents),
    (parents) => parents.toArray()
  );

export const getAllParentsByTaskIdSelector = (state, id) => state.get(`${id}`, defaultParents);

export const makeGetSortedTaskIdsSelector = () =>
  createImmutableIsEqualSelector(
    (state, ids) => ids.map((id) => getAllTasks(state).get(`${id}`)),
    (tasks) =>
      tasks
        .filter((task) => task !== null)
        .sort((a, b) => sortCompare(a.position, b.position))
        .sort((a, b) => {
          const result = sortCompare(a.start_on, b.start_on);

          if (result !== 0) {
            if (!a.start_on) return 1;
            if (!b.start_on) return -1;
          }

          return result;
        })
        .map((task) => task && task.get(`id`))
  );

const defaultChildren = List();

export const makeGetAllChildrenByTaskIdSelector = () =>
  createSelector(
    (state, id) => getAllTaskDependenciesChildren(state).get(`${id}`, defaultChildren),
    (children) => children.toArray()
  );

const defaultSuggestion = List();
export const makeGetAllSuggestionsByTaskIdSelector = () =>
  createSelector(
    (state, id) => getAllTaskDependenciesSuggestions(state).get(`${id}`, defaultSuggestion),
    (state) => state.tasks,
    (state) => getConfigOption(state, `show_task_id`),
    (suggestions, tasks, showTaskId) => {
      return suggestions.reduce((a, b) => {
        const { id, name, task_number } = tasks.get(`${b}`, Map()).toObject();

        if (id) {
          return a.push({
            id,
            name: showTaskId ? `#${task_number}: ${name}` : name,
          });
        }
      }, List());
    }
  );

const defaultGroupedSuggestion = Map();
export const makeGetAllGroupedSuggestionsByTaskIdSelector = () =>
  createSelector(
    (state, id) => getAllTaskDependenciesGroupedSuggestions(state).get(`${id}`, defaultGroupedSuggestion),
    (state, id) => getAllTaskDependenciesRecentTasks(state).get(`${id}`, List([])),
    (state) => state.tasks,
    (state) => state.task_lists,
    (state) => getConfigOption(state, `show_task_id`),
    (groupedSuggestions, recentTasks, tasks, taskLists, showTaskId) => {
      const unsorted = groupedSuggestions.reduce((acc, value, key) => {
        const { id, name, position } = taskLists.get(`${key}`, Map()).toObject();
        if (id) {
          const unsortedTasks = value.reduce((tasksAcc, value) => {
            const { id, name, position, task_number } = tasks.get(`${value}`, Map()).toObject();
            if (id) {
              return [
                ...tasksAcc,
                {
                  id,
                  name: showTaskId ? `#${task_number}: ${name}` : name,
                  position,
                },
              ];
            }
          }, []);
          const sortedTasks = unsortedTasks
            .sort((a, b) => sortCompare(a.position, b.position))
            .map((a) => {
              return { id: a.id, name: a.name };
            });
          return [
            ...acc,
            {
              id,
              name,
              position,
              options: sortedTasks,
            },
          ];
        }
      }, []);

      const sorted = unsorted
        .sort((a, b) => sortCompare(a.position, b.position))
        .map((a) => {
          return { id: a.id, name: a.name, options: a.options };
        });

      if (recentTasks.size > 0) {
        const recentOptions = recentTasks
          .map((id) => {
            const name = tasks.getIn([`${id}`, `name`], ``);
            const number = tasks.getIn([`${id}`, `task_number`], ``);
            return {
              id: `recent-${id}`,
              name: showTaskId ? `#${number}: ${name}` : name,
            };
          })
          .toArray();
        return [
          {
            id: "recent",
            name: i18n.t("Recent Tasks"),
            options: recentOptions,
          },
          ...sorted,
        ];
      }

      return sorted;
    }
  );

export const makeGetTaskHasDependenciesSelector = () =>
  createSelector(
    makeGetAllParentsByTaskIdSelector(),
    makeGetAllChildrenByTaskIdSelector(),
    (parents, children) => !!(parents.length + children.length)
  );

const defaultProjectTasksDependencies = List();
const getAllProjectTasksDependencies = (state, projectId) =>
  state.task_dependencies.projectTasksDependencies.get(`${projectId}`) || defaultProjectTasksDependencies;

export const getProjectTasksGanttDependencies = createSelector(getAllProjectTasksDependencies, (dependencies) =>
  dependencies
    .map((dependency) => {
      const source = dependency.get(`parent_id`);
      const target = dependency.get(`child_id`);
      return {
        source,
        target,
        type: 0,
        color: `#757575`,
        id: makeGanttLinkId(source, target),
      };
    })
    .toArray()
);

const filterHiddenFromClients = (tasks) => {
  return tasks.filter((task) => task && !task.is_hidden_from_clients);
};

export const isInConflictWithParents = (parentTasks, task) => {
  return parentTasks.some((parentTask) => {
    if (!parentTask || !parentTask.due_on) {
      // @todo parents shouldn't be undefined
      return false;
    }
    return task.start_on && task.start_on <= parentTask.due_on;
  });
};

export const makeGetTaskIsInConflict = () =>
  createSelector(
    getTaskById,
    isLoggedUserClientSelector,
    makeGetAllParentsByTaskIdSelector(),
    getOpenTasks,
    (task, isLoggedUserClient, parentIds, openTasks) => {
      let parentTasks = parentIds.reduce((prev, current) => {
        let parent = openTasks.get(`${current}`);
        if (parent) {
          prev.push(parent);
        }
        return prev;
      }, []);
      if (isLoggedUserClient) {
        parentTasks = filterHiddenFromClients(parentTasks);
      }
      return isInConflictWithParents(parentTasks, task);
    }
  );

const getTaskDueDateFromSubtasks = (subtasks) => {
  let taskDueOn;
  subtasks.forEach((sub) => {
    if (!taskDueOn) {
      taskDueOn = sub.due_on;
    }
    if (getDiffBetweenDates(taskDueOn, sub.due_on) > 1) {
      taskDueOn = sub.due_on;
    }
  });
  return taskDueOn;
};

const sortTasks = (a, b) => {
  return a.sortKey.localeCompare(b.sortKey, undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

export const getConflictTasksInProject = createSelector(
  getAllTaskDependenciesParents,
  isLoggedUserClientSelector,
  getTasksForGantt,
  getSubtasksForGantt,
  getGanttTaskLists,
  (state) => state.task_lists,
  (state, projectId) => projectId,
  (parents, isLoggedUserClient, tasks, subtasks, gantLists, taskLists, projectId) => {
    return tasks
      .filter((task) => {
        const taskList = taskLists.get(`${task.get(`task_list_id`)}`, Map());
        return (
          task.get(`project_id`) === projectId &&
          typeof task.get(`id`) == "number" &&
          taskList.has(`is_completed`) &&
          !taskList.get(`is_completed`)
        );
      })
      .map((task) => {
        let taskStartDate = task.get(`start_on`);
        let taskDueDate = task.get(`due_on`);
        let type = "Task";

        if (taskStartDate === null && taskDueDate === null) {
          type = "TaskWithoutDates";
          let taskSubtasks = subtasks.filter((sub) => sub.task_id === task.id);
          taskStartDate = taskDueDate = getTaskDueDateFromSubtasks(taskSubtasks);
        }

        let parentTasks = parents.get(`${task.get(`id`)}`, List());
        parentTasks = parentTasks.map((t) => {
          return tasks.get(`${t}`);
        });
        if (isLoggedUserClient) {
          parentTasks = filterHiddenFromClients(parentTasks);
        }

        let text = task.get(`name`) || "";
        const taskList = taskLists.get(`${task.task_list_id}`);
        return {
          id: task.get(`id`),
          original_id: task.get(`id`),
          type: type,
          class: task.class,
          list_id: task.task_list_id,
          sortKey: `${taskList.get("position")}-${task.task_list_id}-${task.position}`,
          text: text.replaceAll(/</gm, "&lt;"),
          projectId: task.get(`project_id`),
          start_date: dateToMySQLFormat(taskStartDate, `DD-MM-YYYY`),
          duration: getGanttDurationByDates(taskStartDate, taskDueDate),
          taskList: `task-list-${task.get(`task_list_id`)}`,
          assignee_id: task.get(`assignee_id`),
          labels: task.get(`labels`).toArray(),
          due_on: taskDueDate,
          updated_on: task.get(`updated_on`),
          is_in_conflict: isInConflictWithParents(parentTasks.toArray(), task),
          is_completed: task.get("is_completed"),
          position: task.position,
          parent: `task-list-${task.get(`task_list_id`)}`,
          open: task.open_subtasks > 0,
        };
      })
      .toArray()
      .reduce(
        (prev, next) => {
          prev.push(next);
          return prev;
        },
        [...gantLists, ...subtasks]
      )
      .sort(sortTasks);
  }
);

export const makeGetIsParent = () =>
  createSelector(
    makeGetAllParentsByTaskIdSelector(),
    (state, dependencyId, id) => id,
    (parents, id) => parents.indexOf(id) > -1
  );
